// ── Per-state tax-bill calculator (pure, no React/DB) ────────────────────────
// One place for CA / FL / Cook / IL-non-Cook bill math, so ParcelModal AND the TBT generator compute
// identically. Formulas verified by Philip 2026-07-20; behaviour is driven off the ACTUAL collector
// installment encoding confirmed in the live DB (not the is_cook flag).
//
// INPUTS:
//   jurisdictions[]  {name, taxableValue}  — Proposed Values card. taxableValue is the PRE-equalization
//                      ASSESSED value (ratio already applied on the card; the installment `ratio` field is
//                      informational — never reapplied here, or it would double-count).
//   rate    R %      — manual entry OR scanned tax_bills.total_tax_rate
//   nav              — non-ad-valorem (scanned tax_bills.total_non_ad_valorem)
//   deductions D     — subtracted AFTER equalization (Cook); 0 elsewhere
//   priorYearTotal   — sum of the prior year's tax section (parcel_taxes, year N-1). null => Cook prepay
//                      can't be computed; surface "prior year not entered", never treat null as 0.
//   discountAmount   — structured discount from the scanned bill; overrides the % fallback when present
//   installments[]   {installmentNumber, billType, taxType, billPct(=split_pct),
//                      adjType(=adjustment_type), adjPct(=adjustment_pct), dueDate}
//
// ADJUSTMENT ENCODING (adjType decides how adjPct is read — they are NOT all percentages):
//   'Discount'                    -> adjPct is a PERCENT  (factor = 1 - adjPct/100)     [FL]
//   'Equalization Factor'         -> adjPct is a MULTIPLIER (EAV = assessed × adjPct)   [Cook 2.8683 / IL 1]
//   'Last years total tax amount' -> adjPct is a MULTIPLIER of prior year (× 0.55)      [Cook inst-1]
//   'Abatement'                   -> adjPct is DOLLARS off that installment's ad valorem  [NV Clark tax cap]
//      Entered per parcel on the Taxes tab (it differs per parcel). Plain bills only: on a Cook prepay or a
//      discounted FL bill nobody has stated where it comes off, so the computed figure is refused there.
//
// STATE BEHAVIOUR is inferred from the installment data, not a state string:
//   • a Non-Value-Based installment present  -> Cook: prior-year prepay + value-based true-up
//   • a Discount adjustment present          -> FL: single bill, discount on the whole (gross) bill
//   • otherwise                              -> CA / IL: plain (optionally equalized) ad-valorem split

import { aggregateTax } from '../reports/taxRate';
import { jurisdictionLabel } from './jurisdictionLabel';

// ⛔ IMPORTING reports/taxRate FROM utils IS DELIBERATE, and it is acknowledging what that file already is
// rather than creating a dependency. It is the RATE LIBRARY — already imported by DocumentScanner (a page),
// JurisdictionBreakdown (a component) and check-tax-bills.mjs (a script). Its directory understates its role;
// moving it would touch every importer for no behavioural gain, and it imports nothing itself, so there is no
// cycle. Duplicating Σ(rate_j × taxable_j) here instead would be the two-homes failure this codebase spends
// its comments preventing — aggregateTax carries the unit normalisation, the per-line band, the aggregate
// band and the partial/skipped reporting, and a second copy would drift from all four.

const n = (v) => (v == null || v === '' ? 0 : Number(v));
const round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;
const jLabel = (js) => (js.length === 1 ? (jurisdictionLabel(js[0].name) || 'County') : `${js.length} jurisdictions`);

const row = (inst, js, T, R, amount, subTotal, adjType) => ({
  installmentNumber: inst.installmentNumber ?? null,
  billType: inst.billType ?? null,
  jurisdiction: jLabel(js),
  taxableValue: T == null ? null : round2(T),   // null on the multi-base path: Σ(bases) is not a value
  rate: R,
  amount: amount == null ? null : round2(amount),   // Value × Rate; null for the Cook prepay row
  billPct: inst.billPct == null ? null : Number(inst.billPct),
  adjType: adjType ?? null,
  subTotal: subTotal == null ? null : round2(subTotal),   // null = computed unavailable (e.g. equalization not entered)
  dueDate: inst.dueDate ?? null,
});

const EQ = 'Equalization Factor';
const DISC = 'Discount';
const ABATE = 'Abatement';
const abatementOf = (i) => (i.adjType === ABATE ? n(i.adjPct) : 0);

export function computeTaxBill(input) {
  const {
    jurisdictions = [], rate = 0, nav = 0, deductions = 0,
    priorYearTotal = null, discountAmount = null, installments = [], multiJurisdiction = false,
  } = input || {};

  const R = n(rate);
  const NAV = n(nav);
  const D = n(deductions);

  // Equalization: multiplier from the 'Equalization Factor' adjustment (1 when absent → no-op for CA/FL).
  // GUARD: an Equalization Factor adjustment PRESENT but with no pct (e.g. Cook 2025 before SigerTax gives it)
  // means equalization is UNKNOWN — we must NOT silently fall back to E=1 (that computes ~3× too low and
  // looks valid, poisoning the billed-vs-computed check). full stays null → computed shown as unavailable.
  const eqPresent = installments.some((i) => i.adjType === EQ);
  const eqInst = installments.find((i) => i.adjType === EQ && i.adjPct != null && i.adjPct !== '');
  const eqUnknown = eqPresent && !eqInst;
  const E = eqInst ? n(eqInst.adjPct) : 1;

  // ═══ MULTI-BASE: Σ(taxable_j × rate_j), NOT one rate against a summed taxable value ═══
  //
  // ⛔ THIS USED TO REFUSE OUTRIGHT, and refusing was right while there was no per-jurisdiction rate to
  // compute with. With >1 taxing authority each column is the SAME property valued differently — Florida's
  // non-homestead cap limits NON-SCHOOL assessed value to +10% a year while schools are exempt, so Boynton
  // Beach 435200IN 2026 is 11,778,423 to Public Schools and 10,919,985 to County / Municipality / Special
  // Districts. Σ(taxable) double-counts the property; Σ(rate) applies to neither base. Only Σ(rate_j × base_j)
  // is a tax.
  //
  // ⛔ AND IT IS THE ONLY THING THAT CHANGES. Jurisdictions enter this function at exactly one point — the
  // annual ad-valorem figure `full` — so equalization, the Cook prepay, the FL discount, the installment
  // splits and NAV all continue to operate on it unaltered below. One substitution, not a second calculator.
  const multi = multiJurisdiction && jurisdictions.length > 1;
  // ⛔ REFUSED COMBINATIONS, NAMED. Equalization on a multi-base parcel is Cook-plus-Florida, which exists
  // nowhere and whose formula nobody has stated; a non-zero `deductions` is subtracted from ONE base and we
  // cannot know which. Both are refusals rather than assumptions.
  const multiUnsupported = multi && (eqPresent || D !== 0)
    ? (eqPresent ? 'multi-jurisdiction with an equalization factor' : 'multi-jurisdiction with a deduction')
    : null;
  // ⛔ AN ABATEMENT ON A COOK PREPAY OR A DISCOUNTED BILL IS REFUSED, NOT GUESSED: whether it comes off before or
  // after the discount, or off the prepay or the true-up, is a rule nobody has given.
  const abatePresent = installments.some((i) => abatementOf(i) !== 0);
  const abateUnsupported = abatePresent
    && (installments.some((i) => i.taxType === 'Non-Value Based') || installments.some((i) => i.adjType === DISC)
        || (discountAmount != null && discountAmount !== ''))
    ? 'an abatement on a prepay or discounted bill' : null;
  const agg = (multi && !multiUnsupported && !abateUnsupported)
    ? aggregateTax(jurisdictions.map((j) => ({ jurisdiction: j.name, taxableValue: j.taxableValue, rate: j.rate, source: j.rateSource, state: j.state })),
                   { context: 'computeTaxBill' })
    : null;

  // ⛔ `partial` REFUSES TOO, AND THIS IS THE CASE THAT ACTUALLY HAPPENS. aggregateTax returns a NON-NULL tax
  // when some lines computed and others were skipped — its own docstring says the caller "should not present
  // it as final". On a two-base parcel with a rate entered for only one column that is the school levy alone,
  // roughly 38% of the bill, and it would render as a complete computed figure beside a billed one, making the
  // billed amount look wrong. An understated tax is worse than a blank: the blank asks a question.
  if (multiUnsupported || abateUnsupported || (agg && (agg.tax == null || agg.partial))) {
    const insts = installments.length ? installments : [{ installmentNumber: 1, billPct: 100 }];
    const rows = insts.map((i) => ({
      installmentNumber: i.installmentNumber ?? null, billType: i.billType ?? null,
      jurisdiction: jLabel(jurisdictions), taxableValue: null, rate: null, amount: null,
      billPct: i.billPct == null ? null : Number(i.billPct), adjType: i.adjType ?? null,
      subTotal: null, dueDate: i.dueDate ?? null,
    }));
    // A rate missing on ONE column is the common case and says which: aggregateTax reports it per line.
    const missing = agg ? agg.lines.filter((l) => l.skipped).map((l) => jurisdictionLabel(l.jurisdiction) || '—') : [];
    return {
      path: multi ? 'multi' : 'refused', rows, navTotal: round2(NAV), discount: 0, subTotal: null, total: null,
      computedUnavailable: true,
      computedUnavailableReason: multiUnsupported || abateUnsupported
        || (agg?.refused ? `multi-jurisdiction: ${agg.refused}` : `no rate for ${missing.join(', ')}`),
      meta: { aggregate: agg || null },
    };
  }

  // Single base: the taxable value is the one column's. Multi: there is no single T, and `row()` prints null.
  const T = multi ? null : jurisdictions.reduce((s, j) => s + n(j.taxableValue), 0);

  // ⛔ AN ABSENT RATE AND A ZERO RATE ARE THE SAME THING HERE, AND NEITHER MAY COMPUTE.
  // n() collapses null/'' to 0 and `rate = 0` is this function's default, so a parcel with NO rate entered
  // computed full = (taxableEAV × 0)/100 = 0 and returned subTotal 0 / total 0 with computedUnavailable
  // FALSE. The card rendered "Computed $0.00" — a positive claim that the tax is nothing — and where a
  // billed figure existed the Δ (computed − billed) showed the whole bill as a discrepancy, e.g.
  // −$202,139.58. That is a number someone acts on. With a non-ad-valorem charge entered it was worse: the
  // NAV alone rendered as the computed tax.
  // A rate of exactly 0 is treated identically on purpose: no jurisdiction levies nothing, so a typed 0 is a
  // half-finished entry, not a fact. Same shape as the equalization guard directly below — blank, with a
  // reason, is the honest state until a rate exists.
  // ⛔ EXCEPT ON A $0 BASE (Philip, 2026-10-11): every column carries a taxable value and they are all 0 — a fully
  // exempt parcel. Zero times any rate is zero, so the computed ad valorem is $0 with no rate entered.
  const zeroBase = !multi && jurisdictions.length > 0
    && jurisdictions.every((j) => j.taxableValue != null && j.taxableValue !== '' && n(j.taxableValue) === 0);
  const rateMissing = !multi && !zeroBase && !(Number.isFinite(Number(rate)) && Number(rate) !== 0);
  const cantCompute = eqUnknown || rateMissing;
  const unavailableReason = eqUnknown ? 'equalization factor not entered'
    : rateMissing ? 'no tax rate entered' : null;

  const eav = (cantCompute || multi) ? null : T * E;
  const taxableEAV = (cantCompute || multi) ? null : eav - D;
  // ⛔ ALREADY CURRENCY ON THE MULTI PATH. aggregateTax returns Σ(base × decimal rate) — a money figure — so
  // it is NOT divided by 100 again. Doing so is the factor-of-100 twin of the millage error.
  const full = multi ? agg.tax : (cantCompute ? null : zeroBase ? 0 : (taxableEAV * R) / 100);
  const gross = full == null ? null : full + NAV;
  // ⛔ NO SINGLE RATE TO DISPLAY ON THE MULTI PATH. R is the COLLECTOR's consolidated rate — school plus
  // non-school added together — which applies to neither base, so printing it beside a correct amount would
  // invite someone to check the arithmetic and find it "wrong". The per-jurisdiction rates are in
  // meta.aggregate.lines, which is where a breakdown belongs.
  const Rdisp = multi ? null : R;
  // ⛔ THE AGGREGATE BAND'S DENOMINATOR IS Σ(bases), WHICH FOR N BASES IS ~N× THE REAL BASE — so the
  // effective rate aggregateTax judges is roughly 1/N of the true one, and its 0.5%–5% band is in effect
  // 1%–10% per base at N=2. Florida (~1.8%–2.2% combined) sits comfortably inside; a low-millage pair could
  // be refused. That fails CLOSED — a blank, not a wrong figure — so it is logged, not worked around here.

  const prepayInsts = installments.filter((i) => i.taxType === 'Non-Value Based');
  const discInst = installments.find((i) => i.adjType === DISC && i.adjPct != null && i.adjPct !== '');

  // ── Cook: prior-year prepay (Non-Value-Based) + value-based true-up ──
  // The true-up absorbs rounding: inst-2 = round(FULL) − Σround(prepay), computed on already-rounded
  // amounts so the displayed rows always sum to the displayed total (no stray penny from double-rounding).
  if (prepayInsts.length) {
    const rows = [];
    const fullR = cantCompute ? null : round2(full);     // null when the equalization factor or the rate isn't entered
    let prepayR = 0;
    const priorKnown = priorYearTotal != null && priorYearTotal !== '';
    prepayInsts.forEach((i) => {
      const mult = n(i.adjPct);                       // 0.55 direct multiplier (independent of equalization)
      const amt = priorKnown ? round2(n(priorYearTotal) * mult) : null;
      if (amt != null) prepayR += amt;
      rows.push(row(i, jurisdictions, T, Rdisp, null, amt ?? 0,
        priorKnown ? `${i.adjType} ×${mult}` : 'prior year not entered'));
    });
    installments.filter((i) => i.taxType !== 'Non-Value Based').forEach((i) => {
      const amt = fullR == null ? null : fullR - prepayR;  // true-up needs the equalized total; null if unknown
      rows.push(row(i, jurisdictions, T, Rdisp, fullR == null ? null : full, amt,
        unavailableReason || (E !== 1 ? `${EQ} ×${E}` : (i.adjType ?? null))));
    });
    return {
      path: 'Cook',
      rows, navTotal: round2(NAV), discount: 0,
      subTotal: fullR, total: fullR == null ? null : round2(fullR + NAV),
      priorYearMissing: !priorKnown, computedUnavailable: cantCompute, computedUnavailableReason: unavailableReason,
      meta: { E: cantCompute ? null : E, eav: cantCompute ? null : round2(eav), taxableEAV: cantCompute ? null : round2(taxableEAV), full: fullR,
              priorYearTotal: priorKnown ? round2(n(priorYearTotal)) : null, prepay: prepayR, aggregate: agg },
    };
  }

  // ── FL: single annual bill; discount on the WHOLE bill (ad-valorem + non-ad-valorem) ──
  if (discInst || (discountAmount != null && discountAmount !== '')) {
    const dpct = discInst ? n(discInst.adjPct) : null;
    const disc = discountAmount != null && discountAmount !== '' ? n(discountAmount)
      : (dpct ? (gross * dpct) / 100 : 0);
    const insts = installments.length ? installments : [{ installmentNumber: 1, billType: 'Annual Bill' }];
    // ⛔ NULL-SAFE, WHICH IT DID NOT NEED TO BE UNTIL NOW. Florida has no equalization, so `full` could never
    // be null on this path and nothing guarded it. With the rate guard it can be — and unguarded,
    // `(null * pct)/100` is 0, `round2(null)` is 0, and `round2(gross - disc)` with a null gross is
    // round2(-disc): a NEGATIVE total, on the state whose bills are single annual bills with a discount.
    const rows = insts.map((i) => row(i, jurisdictions, T, Rdisp, full,
      full == null ? null : (full * n(i.billPct || 100)) / 100,
      dpct != null ? `${DISC} ${dpct}%` : DISC));
    return {
      path: 'FL',
      rows, navTotal: round2(NAV), discount: full == null ? 0 : round2(disc),
      subTotal: full == null ? null : round2(full),
      total: (full == null || gross == null) ? null : round2(gross - disc),
      computedUnavailable: cantCompute, computedUnavailableReason: unavailableReason,
      meta: { E, eav: (cantCompute || multi) ? null : round2(eav), taxableEAV: (cantCompute || multi) ? null : round2(taxableEAV),
              full: full == null ? null : round2(full), gross: gross == null ? null : round2(gross), discountPct: dpct, aggregate: agg },
    };
  }

  // ── CA / IL non-Cook: plain (optionally equalized) ad-valorem, split by Bill%. NAV is its own line. ──
  // An Abatement comes off its own installment's share in dollars (NV: gross share − tax cap = billed).
  const insts = installments.length ? installments : [{ installmentNumber: 1, billPct: 100 }];
  const A = insts.reduce((s, i) => s + abatementOf(i), 0);
  const rows = insts.map((i) => row(i, jurisdictions, T, Rdisp, full == null ? null : full,
    full == null ? null : (full * n(i.billPct)) / 100 - abatementOf(i),
    unavailableReason || (E !== 1 ? `${EQ} ×${E}` : (abatementOf(i) ? `${ABATE} −${round2(abatementOf(i)).toFixed(2)}` : (i.adjType ?? null)))));
  return {
    path: (eqPresent || E !== 1) ? 'IL' : 'CA',
    rows, navTotal: round2(NAV), discount: 0,
    subTotal: full == null ? null : round2(full - A), total: full == null ? null : round2(full - A + NAV),
    computedUnavailable: cantCompute, computedUnavailableReason: unavailableReason,
    meta: { E: cantCompute ? null : E, eav: (cantCompute || multi) ? null : round2(eav), taxableEAV: (cantCompute || multi) ? null : round2(taxableEAV), full: full == null ? null : round2(full), abatement: round2(A), aggregate: agg },
  };
}

// Where a collector's ANNUAL non-ad valorem charge falls across its installments, for DISPLAY on the parcel card
// (collectors.nav_billed_on, 2026-10-16). 'installment_1' → all of it on the first; anything else → split evenly with the
// last absorbing the remainder (the Cook true-up precedent: the shares always sum to the annual). The stored figure stays
// annual everywhere; nothing writes these shares. → number[] (length count)
export function navShares(annual, count, billedOn) {
  const a = round2(n(annual));
  if (!count || count < 1) return [];
  if (billedOn === 'installment_1') return Array.from({ length: count }, (_, i) => (i === 0 ? a : 0));
  let acc = 0;
  return Array.from({ length: count }, (_, i) => {
    const v = i === count - 1 ? round2(a - acc) : round2(a / count);
    acc = round2(acc + v);
    return v;
  });
}
